import { HostifyListing, Integration, LocalEditableIntegration } from "../../_modelsTS/Integration";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CapsuleButton, CustomNumberInput, Selector } from "../../_components";
import { useDispatch, useSelector } from "react-redux";
import { integrationsActions } from "../../_actions/integrations.actions";
import { Selectors } from "../../_reducers/app.reducer";
import { INVITATION_TYPES, MIN_TIME, TIME_FORMAT } from "../../_constants";
import { useError } from "../../_hooks/useError";
import { Icons } from "../../_assets";
import { IntegrationStore } from "../../_stores/IntegrationStore";
import { HostifyStore } from "../../_stores/HostifyStore";
import SendInvitationURLToggle from "./SendInvitationURLToggle";
import { hostifyActions } from "../../_actions/hostify.actions";
import SendInvitationURLFromPMSToggle from "./SendInvitationURLFromPMSToggle";
import ToolTipWrapper from "../../_components/ToolTipWrapper";
import TimePicker from "rc-time-picker";
import moment from "moment";
import { TimeHelper } from "../../_helpers";


type IntegrationInputProps = {
    integration: LocalEditableIntegration;
    onChangeInputs?: (...args: any) => void | any;
    onToggleEditable: (...args: any) => void | any;
    onSetInitialStatus: (...args: any) => void | any;
    groups: { [key: number]: any[]; }; // To be typed asap
    onDelete: (index: number) => void;
    index: number;
    linkMode?: 'link' | 'ical';
}

export function HostifyIntegrationInput({ integration, onDelete, index, onSetInitialStatus, groups, linkMode = 'link', onToggleEditable }: IntegrationInputProps) {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const buildOptions = useCallback((availableGroups: any[] = []) => {
            return availableGroups.map((group) => ({ value: group.id, label: group.name, isDisabled: group.isDisabled })) ?? [];
    }, []);

    const integrations = useSelector(IntegrationStore.selectorGetItems);

    const selectedIntegrationUid: string = useSelector(IntegrationStore.selectorGetSelectedId);

    const buildGroupGroupedOptions = useCallback((groups: { [key: number]: any[] }) => {
        return [
            {
                label: t("global_typeof_location"),
                options: buildOptions(groups[INVITATION_TYPES.LOCATION]),
            },
            {
                label: t("global_typeof_room"),
                options: buildOptions(groups[INVITATION_TYPES.ROOM]),
            },
        ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const allGroups = useMemo(() => [...(groups[INVITATION_TYPES.LOCATION] ?? []), ...(groups[INVITATION_TYPES.ROOM]) ?? []], [groups]);
    const baseIntegration = useMemo(() => integration, [integration]);
    const [apikeyValue, setApikeyValue] = useState(integration.apikey ?? '');
    const [hostIDValue, setHostIDValue] = useState(integration.host_id ?? '');
    const [availableGroups, setAvailableGroups] = useState(buildGroupGroupedOptions(groups));
    const integrationGroups = integration.groups || [];
    const [selectedGroupsLocal, setSelectedGroupsLocal] = useState(integrationGroups.map(({id}) => ({value: id})) ?? []);
    // Hostify default: both senders off — most Hostify accounts deliver the link
    // through their own PMS automations (BAINTEX-URL custom field).
    const [sendInvitationURL, setSendInvitationURL] = useState(integration.sendInvitationURL ?? false);
    const [sendInvitationURLFromPMS, setSendInvitationURLFromPMS] = useState(integration.sendInvitationURLFromPMS ?? false);
    const [daysForInvitationCreate, setDaysForInvitationCreate] = useState(integration.daysForInvitationCreate ?? 1);
    const { errorMessage, setError, hasError, disableError } = useError();
    const locationUid = useSelector(Selectors.getSelectedLocationUid) ?? '';
    // What the second half of the form used to hang off `integration.uid`, which
    // only existed because validating persisted the row. It no longer does, so the
    // gate is "we have listed this account's properties" instead. An integration
    // that is already saved starts out validated.
    const [validated, setValidated] = useState(!!integration.uid);

    const buildPropertyOptions = useCallback((availableProperties: any[] = []) => {
        return availableProperties.map((property) => ({ value: property.id, label: property.nickname, isDisabled: false })) ?? [];
    }, []);
    
    const properties = useSelector(HostifyStore.selectorGetItems);
    const selectedProperty : HostifyListing = useSelector(HostifyStore.selectorGetSelectedItem) ?? {id:integration.host_id, name:integration.host_name};
    const [selectedPropertiesLocal, setSelectedPropertiesLocal] = useState({ value:integration.host_id, label:integration.host_name, isDisabled: false });
    const [availableProperties, setAvailableProperties] = useState(buildPropertyOptions(properties));

    const onChangeProperty = useCallback(selectedProperty => {
        setSelectedPropertiesLocal(selectedProperty);
        dispatch(HostifyStore.actionSelectItem(selectedProperty.value));
        // Only an already-saved integration can be re-listed by uid. During an alta
        // there is no uid yet -- the list is already in the store from validating.
        if (selectedProperty !== undefined && !!integration.uid) {
            dispatch(hostifyActions.listListings(integration.uid));
        }
    }, [dispatch, integration.uid]);

    // Editing the key invalidates everything listed with the previous one. Without
    // this the picker keeps showing the old account's properties while the field
    // holds a new key, and saving pairs a key with a listing that does not exist in
    // its own account -- the state BAC-314 is about, arrived at from the other end.
    const onChangeApikey = useCallback((value: string) => {
        setApikeyValue(value);
        if (value !== baseIntegration.apikey) {
            setValidated(false);
            setSelectedPropertiesLocal({ value: undefined, label: undefined, isDisabled: false });
            dispatch(HostifyStore.actionListItemsSuccess([]));
        }
    }, [dispatch, baseIntegration.apikey]);

    const onSeeReservations = useCallback(async (integrationUid) => {
        await dispatch(integrationsActions.listReservations(locationUid, integrationUid));
        dispatch(IntegrationStore.actionSelectItem(integrationUid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationUid]);


    const onChangeGroups = useCallback(selectedGroups => {
        setAvailableGroups(buildGroupGroupedOptions(groups));
        setSelectedGroupsLocal(selectedGroups);
    }, [buildGroupGroupedOptions, groups]);

    const [checkIn, setCheckIn] = useState(integration.check_in ?? MIN_TIME);
    const [checkOut, setCheckOut] = useState(integration.check_out ?? MIN_TIME);
    const timeNoSecondsFormat = TimeHelper.getTimeFormats().TIME_NO_SECONDS;
    const getHourAndMinutesFormat = useCallback((time: string) => moment(time, timeNoSecondsFormat), []);

    useEffect(() => { 
        setApikeyValue(integration.apikey ?? '');
        setHostIDValue(integration.host_id ?? '');
        setSendInvitationURL(integration.sendInvitationURL ?? false);
        setSendInvitationURLFromPMS(integration.sendInvitationURLFromPMS ?? false);
        setDaysForInvitationCreate(integration.daysForInvitationCreate ?? 1);

        const selected = buildOptions(allGroups.filter(({ id }) => integrationGroups.map(({id}) => id).includes(id)));
        onChangeGroups(selected);
    }, [allGroups, buildOptions, groups, integration, onChangeGroups]);

    const canValidate = useMemo(() => {
        if(hasError) return false;
        // Only the key matters here: validating just lists that account's
        // properties. The groups were required back when this button also created
        // the integration; canSave still demands them, which is where they are
        // actually used.
        return !!apikeyValue;
    }, [apikeyValue, hasError]);

    // Validating checks the key and fills the property picker; it must NOT persist
    // anything. It used to create the integration here, purely because the picker
    // could only list from a stored uid -- so an alta abandoned before choosing a
    // property left a row with no listing, and that row syncs the whole Hostify
    // account into this location (BAC-314). The integration is created on save now,
    // once the property is known.
    const onValidate = useCallback(async () => {

        if(!apikeyValue) {
            setError(t('mod_integrations_link_required'));
            return;
        }

        try {
            await dispatch(hostifyActions.listListingsByApikey(apikeyValue));
            setValidated(true);
        } catch (err: any) {
            setError(err.message)
        }
    }, [dispatch, setError, apikeyValue, t]);


    const canSave = useMemo(() => {

        if(hasError) return false;

        const baseGroups = baseIntegration.groups || [];

        const groupsWithoutDifferences = baseGroups.every(({id}) =>
                selectedGroupsLocal.map(({ value }) => value).includes(id),
            ) && baseGroups.length === selectedGroupsLocal.length;

        return (
            !!apikeyValue && 
            !!selectedProperty && 
            selectedGroupsLocal.length !== 0 && 
            (
                apikeyValue !== baseIntegration.apikey ||
                hostIDValue !== baseIntegration.host_id ||
                !groupsWithoutDifferences || 
                sendInvitationURL !== integration.sendInvitationURL || 
                sendInvitationURLFromPMS !== integration.sendInvitationURLFromPMS ||
                daysForInvitationCreate !== integration.daysForInvitationCreate ||
                checkIn !== integration.check_in ||
                checkOut !== integration.check_out
        ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseIntegration.groups, baseIntegration.link, integrations, integration.createPublicUrl, apikeyValue, hostIDValue, selectedGroupsLocal,
        sendInvitationURL, selectedProperty, sendInvitationURLFromPMS, daysForInvitationCreate, checkIn, checkOut]);
    

    const onSave = useCallback(async () => {

        const editedIntegration: Integration = {
            apikey: apikeyValue,
            host_id: selectedPropertiesLocal.value,
            host_name: selectedPropertiesLocal.label,
            company: integration.company,
            // Not user-configurable for Hostify: without the public link there is no
            // BAINTEX-URL push nor retry; every Hostify account in prod runs with it on.
            createPublicUrl: true,
            groups: selectedGroupsLocal.map(({ value }) => ({ id: value })),
            uid: integration.uid,
            sendInvitationURL,
            sendInvitationURLFromPMS,
            daysForInvitationCreate,
            check_in: checkIn,
            check_out: checkOut
        }
        if(locationUid) {
            try {
                await dispatch(integrationsActions.createIntegration(locationUid, editedIntegration));
            } catch (err: any) {
                setError(err.message)
            }
        }
    }, [dispatch, setError, integration.company,
        integration.uid, apikeyValue, hostIDValue, locationUid, selectedGroupsLocal,
        t, sendInvitationURL, selectedProperty?.id, selectedProperty?.nickname,
        sendInvitationURLFromPMS, daysForInvitationCreate, checkIn, checkOut])

    useEffect(() => {
        if (!!integration.uid) {
            dispatch(hostifyActions.listListings(integration.uid));
        }

    }, [dispatch, integration.uid]);

    useEffect(() => {
        setAvailableProperties(buildPropertyOptions(properties));
    }, [properties, buildPropertyOptions]);

    return (
        <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg">
            <div
                className="col-span-full"
                style={{
                    display: "grid",
                    gap: "12px",
                    gridTemplateColumns: "3fr 2fr",
                }}
            >
                <div>API Key</div>
                <input
                    className={`${hasError ? 'input-error' : ''} ${!integration.editable ? 'cna' : ''} col-span-full w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    readOnly={!integration.editable}
                    value={apikeyValue}
                    placeholder={"API Key"}
                    onChange={(e) => {
                        onChangeApikey(e.target.value?.trim());
                        /*if(IntegrationsHelper.checkIfLinkExists(e.target.value, integrations) && e.target.value !== integration.link) {
                            setError(t('mod_integrations_already_have_integration_with_this_link'));
                        } else {
                            disableError();
                        }*/
                    }}
                />
            </div>
            <div
                style={{
                    display: "grid",
                    gap: "12px",
                    gridTemplateColumns: "1fr 1fr",
                }}
                className="col-span-full d-flex justify-content-between"
            >
                <div className="c-min-w-40">

                <Selector
                    hideSelectedOptions={false}
                    placeholder={t("mod_invitations_select_group")}
                    isClearable
                    value={selectedGroupsLocal[0] ?? null}
                    onChange={(selectedGroup: any) =>
                        onChangeGroups(selectedGroup ? [selectedGroup] : [])
                    }
                    isDisabled={!integration.editable}
                    options={availableGroups}
                    />
                    </div>
                    <div>
                        <SendInvitationURLToggle
                            optional
                            boldedName={false}
                            initialState={sendInvitationURL}
                            readOnly={!integration.editable}
                            onChange={setSendInvitationURL}
                        />
                        <div className="mt-1">
                            <SendInvitationURLFromPMSToggle
                                optional
                                boldedName={false}
                                initialState={sendInvitationURLFromPMS}
                                readOnly={!integration.editable}
                                onChange={setSendInvitationURLFromPMS}
                            />
                        </div>
                    </div>
            </div>
            <div className="col-span-full d-flex align-items-center c-bg-grey-250 c-rounded-big p-2">
                <img
                    src={Icons.info}
                    alt="info"
                    className="mr-2 flex-shrink-0"
                    style={{ width: "18px", height: "18px" }}
                />
                <span className="small">{t("mod_integrations_hostify_custom_field_info")}</span>
            </div>
            { !validated && 
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex">
                        <CapsuleButton
                            size="SMALL"
                            isEnabled={canValidate}
                            onClick={onValidate}
                            text={t("global_validate")}
                        />
                    </div>
                </div>
            }

            { validated &&
                <div
                    className="col-span-full"
                    style={{
                        display: "grid",
                        gap: "12px",
                        gridTemplateColumns: "3fr 2fr",
                    }}
                >
                    <Selector
                        hideSelectedOptions={false}
                        placeholder={t("mod_integrations_select_property")}
                        value={selectedPropertiesLocal}
                        onChange={(selectedProperty: any) =>
                            onChangeProperty(selectedProperty)
                        }
                        isDisabled={availableProperties.length === 0}
                        options={availableProperties}
                    />
                </div>
            }
            { validated && 
                <div className="d-flex align-items-center justify-content-between pr-2">
                    <ToolTipWrapper tooltip={t("mod_integrations_check_in_tooltip")}>
                        <div className="c-text-capitalize-first mr-2">
                            Check In
                        </div>
                    </ToolTipWrapper>
                    <TimePicker
                            disabled={!integration.editable}
                            showSecond={false}
                            minuteStep={5}
                            value={getHourAndMinutesFormat(checkIn)}
                            format={TIME_FORMAT}
                            onChange={(value) =>
                                setCheckIn(
                                    value
                                        ? value?.format(TIME_FORMAT)
                                        : MIN_TIME,
                                )
                            }
                    />
                </div>
            }
            { validated && 
                <div className="d-flex align-items-center justify-content-between pr-2">
                    <ToolTipWrapper tooltip={t("mod_integrations_check_out_tooltip")}>
                        <div className="c-text-capitalize-first mr-2">
                            Check Out
                        </div>
                    </ToolTipWrapper>
                    <TimePicker
                            disabled={!integration.editable}
                            showSecond={false}
                            minuteStep={5}
                            value={getHourAndMinutesFormat(checkOut)}
                            format={TIME_FORMAT}
                            onChange={(value) =>
                                setCheckOut(
                                    value
                                        ? value?.format(TIME_FORMAT)
                                        : MIN_TIME,
                                )
                            }
                    />
                </div>
            }

            { validated &&
                <div className="col-span-full d-flex align-items-center justify-content-between">
                    <ToolTipWrapper tooltip={t("mod_integrations_days_for_invitation_create_tooltip")}>
                        <label className="font-weight-bold mb-0">{t("mod_integrations_days_for_invitation_create")}</label>
                    </ToolTipWrapper>
                    <CustomNumberInput
                        value={daysForInvitationCreate}
                        min={1}
                        max={30}
                        readOnly={!integration.editable}
                        onChange={setDaysForInvitationCreate}
                    />
                </div>
            }

            <div className="d-flex justify-content-between align-items-center">
                <div>
                    {integration.host_id &&
                        <CapsuleButton
                            isEnabled={integration.uid !== selectedIntegrationUid}
                            onClick={() => onSeeReservations(integration.uid)}
                            classNameExtra="mr-2"
                            // eslint-disable-next-line react/style-prop-object
                            size="SMALL"
                            text={t('mod_integrations_see_reservations')}
                        />
                    }
                </div>
                <div className="d-flex">
                    {
                        !!integration.uid &&
                        <CapsuleButton
                            size="SMALL"
                            classNameExtra="mr-2"
                            style={!integration.editable ? 'PRIMARY' : 'INFO'}
                            onClick={() => onToggleEditable(index)}
                            text={t(!integration.editable ?  "global_edit" : 'global_cancel')}
                        />
                    }
                    {integration.uid && (integration.editable || !integration.uid) &&
                        <CapsuleButton
                            onClick={() => onDelete(index)}
                            classNameExtra="mr-2"
                            // eslint-disable-next-line react/style-prop-object
                            style="DANGER"
                            size="SMALL"
                            icon={Icons.trash}
                        />
                    }
                    { validated &&
                        <CapsuleButton
                            size="SMALL"
                            isEnabled={canSave}
                            onClick={onSave}
                            text={t("global_save")}
                        />
                    }
                </div>
            </div>
                    {errorMessage && (
                        <div className="text-danger mr-2">{errorMessage}</div>
                    )}
        </div>
    );
}